import { inject, Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { logger } from '@core/utils/logger';
import {
  COLLABORATIVE_SSE_EVENTS,
  CollaborativeSseEvent,
} from '@features/client/models/public-bill-split-sse-models';

@Injectable({ providedIn: 'root' })
export class CollaborativeBillSplitSseService {
  private zone = inject(NgZone);
  private readonly apiUrl = `${environment.apiUrl}/public/bill-splits`;

  constructor() {
    this.cleanupOldSseClientIds();
  }

  subscribeHost(splitId: string, hostToken: string): Observable<CollaborativeSseEvent> {
    const clientId = this.getOrCreateClientId('host');
    const url =
      `${this.apiUrl}/${splitId}/host-events` +
      `?hostToken=${encodeURIComponent(hostToken)}` +
      `&clientId=${encodeURIComponent(clientId)}`;

    return this.createStream(url);
  }

  subscribeParticipant(
    splitId: string,
    participantToken: string,
  ): Observable<CollaborativeSseEvent> {
    const clientId = this.getOrCreateClientId('participant');
    const url =
      `${this.apiUrl}/${splitId}/participant-events` +
      `?participantToken=${encodeURIComponent(participantToken)}` +
      `&clientId=${encodeURIComponent(clientId)}`;

    return this.createStream(url);
  }

  private createStream(url: string): Observable<CollaborativeSseEvent> {
    return new Observable((observer) => {
      let es: EventSource | null = null;
      let retryDelay = 2000;
      let retries = 0;
      let timer: ReturnType<typeof setTimeout> | null = null;

      const close = () => {
        if (timer) clearTimeout(timer);
        timer = null;
        try {
          es?.close();
        } catch {
          // close() sobre un EventSource ya cerrado no debe cortar la limpieza.
        }
        es = null;
      };

      const connect = () => {
        es = new EventSource(url);

        es.onopen = () => {
          this.zone.run(() => {
            retryDelay = 2000;
            retries = 0;
          });
        };

        COLLABORATIVE_SSE_EVENTS.forEach((type) => {
          es!.addEventListener(type, (event) => {
            this.zone.run(() => {
              const raw = (event as MessageEvent).data;
              try {
                // El dato tiene la forma que declara el contrato para ese evento
                observer.next({ type, payload: JSON.parse(raw) } as CollaborativeSseEvent);
              } catch (error) {
                logger.warn(
                  'División de cuenta',
                  `Evento con datos ilegibles: evento=${type}`,
                  error,
                );
              }
            });
          });
        });

        es.onerror = () => {
          this.zone.run(() => {
            close();
            timer = setTimeout(() => {
              retries++;
              retryDelay = Math.min(retryDelay * 2, 30000);

              if (retries > 10) {
                observer.error(new Error('No se pudo restablecer la conexión en tiempo real.'));
                return;
              }

              connect();
            }, retryDelay);
          });
        };
      };

      connect();
      return () => close();
    });
  }

  private getOrCreateClientId(scope: 'host' | 'participant'): string {
    const key = `dinno-sse-client-${scope}`;
    let value = sessionStorage.getItem(key);

    if (!value) {
      value = crypto.randomUUID();
      sessionStorage.setItem(key, value);
    }

    return value;
  }

  private cleanupOldSseClientIds(): void {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('dinno-sse-client-host-'))
      .forEach((key) => sessionStorage.removeItem(key));

    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('dinno-sse-client-participant-'))
      .forEach((key) => sessionStorage.removeItem(key));
  }
}
